/**
 * Fetch wrapper for the Fornitalia backend API. In dev, Vite proxies `/api`
 * to http://localhost:4000 (see vite.config.ts), so we always use relative
 * URLs. Override with VITE_API_BASE_URL if you need to point at a remote
 * backend from a built frontend.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

const TOKEN_KEY = 'fornitalia.token';

export function getStoredToken(): string | null {
  return typeof window === 'undefined' ? null : window.localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

/**
 * Active country for outgoing requests. Set by `ActiveCountryProvider` on
 * mount and on every switch; `apiFetch` reads it via this module-level
 * getter so call sites don't need to thread the value through. Backend
 * defaults to the user's `pais_codigo` when absent, so leaving this null
 * is safe — same behavior as Phase 0.
 */
let activeCountry: 'AR' | 'MX' | null = null;

export function setActiveCountryHeader(code: 'AR' | 'MX' | null) {
  activeCountry = code;
}

export function getActiveCountryHeader(): 'AR' | 'MX' | null {
  return activeCountry;
}

/**
 * Header set for raw `fetch()` calls that bypass `apiFetch` (multipart
 * uploads, blob downloads). Returns both the bearer token AND the active
 * country header — forgetting the country header makes the backend fall
 * back to the user's primary pais and 404 on rows from the other country.
 *
 * Use this anywhere you'd otherwise hand-roll
 * `{ Authorization: 'Bearer …' }`.
 */
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getStoredToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (activeCountry) headers['X-Active-Country'] = activeCountry;
  return headers;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message?: string,
    public readonly details?: unknown,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** If true, do not attach the stored bearer token. */
  anonymous?: boolean;
  signal?: AbortSignal;
};

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous = false, signal } = options;
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (!anonymous) {
    const token = getStoredToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (activeCountry) headers['X-Active-Country'] = activeCountry;
  }

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  let payload: unknown;
  if (text && isJson) {
    try {
      payload = JSON.parse(text);
    } catch {
      // Fall through — we'll surface a generic error below.
    }
  }

  if (!res.ok) {
    const errObj = payload as { error?: string; message?: string; details?: unknown } | undefined;
    const code = errObj?.error ?? (res.status === 500 ? 'backend_unreachable' : 'unknown_error');
    const message =
      errObj?.message ??
      (isJson
        ? undefined
        : res.status === 500
          ? 'No se pudo establecer conexión con el servidor.'
          : `HTTP ${res.status}`);
    throw new ApiError(res.status, code, message, errObj?.details);
  }

  return payload as T;
}
